import { useEffect, useState } from "react";
import { api, formatMoney } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
    KeyRound,
    Plus,
    RefreshCw,
    Pencil,
    Trash2,
    Calendar,
    AlertTriangle,
    CheckCircle2,
    Clock,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import { toast } from "sonner";
import RentalDialog from "@/components/RentalDialog";

const STATUS_META = {
    ok: {
        label: "À jour",
        badge: "bg-emerald-100 text-emerald-800 border-emerald-200",
        icon: CheckCircle2,
        accent: "text-emerald-700 bg-emerald-100",
    },
    warning: {
        label: "À renouveler",
        badge: "bg-amber-100 text-amber-800 border-amber-200",
        icon: Clock,
        accent: "text-amber-700 bg-amber-100",
    },
    expired: {
        label: "Expirée",
        badge: "bg-rose-100 text-rose-800 border-rose-200",
        icon: AlertTriangle,
        accent: "text-rose-700 bg-rose-100",
    },
    invalid: {
        label: "Invalide",
        badge: "bg-slate-100 text-slate-800 border-slate-200",
        icon: AlertTriangle,
        accent: "text-slate-700 bg-slate-100",
    },
};

const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
        return format(parseISO(iso), "d MMM yyyy", { locale: fr });
    } catch {
        return iso;
    }
};

const daysLabel = (d) => {
    if (d === null || d === undefined) return "—";
    if (d < 0) return `expirée depuis ${Math.abs(d)} j`;
    if (d === 0) return "expire aujourd'hui";
    if (d === 1) return "expire demain";
    return `dans ${d} j`;
};

export default function Rentals() {
    const [rentals, setRentals] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);

    const load = async () => {
        const r = await api.get("/rentals");
        setRentals(r.data);
    };

    useEffect(() => {
        load();
    }, []);

    const remove = async (r) => {
        if (!window.confirm(`Supprimer "${r.name}" ?`)) return;
        try {
            await api.delete(`/rentals/${r.id}`);
            toast.success("Location supprimée");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Erreur");
        }
    };

    const renew = async (r) => {
        try {
            await api.post(`/rentals/${r.id}/renew`);
            toast.success(`"${r.name}" renouvelée`);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Erreur");
        }
    };

    const alertsCount = rentals.filter(
        (r) => r.status === "warning" || r.status === "expired"
    ).length;

    return (
        <>
            <PageHeader
                title="Locations"
                subtitle="Baux, loyers et rappels de renouvellement"
                actions={
                    <Button
                        data-testid="rental-add-btn"
                        onClick={() => {
                            setEditing(null);
                            setDialogOpen(true);
                        }}
                        className="bg-[#3D4E2E] hover:bg-[#2E3A22] text-white"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Nouvelle location
                    </Button>
                }
            />

            <div className="p-8 space-y-8">
                {alertsCount > 0 && (
                    <Card className="border-amber-200 bg-amber-50 p-5 shadow-sm">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-md bg-amber-100 text-amber-700 flex items-center justify-center">
                                <AlertTriangle className="w-5 h-5" />
                            </div>
                            <div>
                                <div className="font-medium text-amber-900">
                                    {alertsCount} location(s) à surveiller
                                </div>
                                <div className="text-sm text-amber-800">
                                    Une ou plusieurs locations approchent de leur expiration
                                    ou sont déjà expirées.
                                </div>
                            </div>
                        </div>
                    </Card>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                    {rentals.length === 0 && (
                        <div className="col-span-full text-center text-slate-500 py-16 border border-dashed border-slate-200 rounded-lg bg-white">
                            Aucune location enregistrée. Ajoutez-en une pour recevoir un
                            rappel avant expiration.
                        </div>
                    )}
                    {rentals.map((r) => {
                        const meta = STATUS_META[r.status] || STATUS_META.ok;
                        const StatusIcon = meta.icon;
                        return (
                            <Card
                                key={r.id}
                                data-testid={`rental-card-${r.id}`}
                                className="border-slate-200 shadow-sm p-6 bg-white flex flex-col"
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2">
                                            <div
                                                className={`w-9 h-9 rounded-md flex items-center justify-center ${meta.accent}`}
                                            >
                                                <KeyRound className="w-4 h-4" />
                                            </div>
                                            <div className="font-display text-lg font-semibold text-slate-900 truncate">
                                                {r.name}
                                            </div>
                                        </div>
                                    </div>
                                    <Badge className={`${meta.badge} border shrink-0`}>
                                        <StatusIcon className="w-3 h-3 mr-1" />
                                        {meta.label}
                                    </Badge>
                                </div>

                                <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
                                    <div>
                                        <div className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                                            Début
                                        </div>
                                        <div className="text-slate-900 font-medium mt-1 flex items-center gap-1.5">
                                            <Calendar className="w-3.5 h-3.5 text-slate-400" />
                                            {fmtDate(r.start_date)}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                                            Fin
                                        </div>
                                        <div className="text-slate-900 font-medium mt-1 flex items-center gap-1.5">
                                            <Calendar className="w-3.5 h-3.5 text-slate-400" />
                                            {fmtDate(r.end_date)}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                                            Durée
                                        </div>
                                        <div className="text-slate-900 font-medium mt-1">
                                            {r.duration_days} j
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                                            Restant
                                        </div>
                                        <div
                                            className={`font-medium mt-1 ${
                                                r.status === "expired"
                                                    ? "text-rose-700"
                                                    : r.status === "warning"
                                                      ? "text-amber-700"
                                                      : "text-slate-900"
                                            }`}
                                        >
                                            {daysLabel(r.days_left)}
                                        </div>
                                    </div>
                                </div>

                                {(r.cost > 0 || r.note) && (
                                    <div className="mt-4 pt-4 border-t border-slate-100 space-y-2 text-sm">
                                        {r.cost > 0 && (
                                            <div className="flex items-center justify-between">
                                                <span className="text-slate-500">Coût</span>
                                                <span className="font-medium text-slate-900 tabular-nums">
                                                    {formatMoney(r.cost)}
                                                </span>
                                            </div>
                                        )}
                                        {r.note && (
                                            <div className="text-slate-600 text-xs italic">
                                                {r.note}
                                            </div>
                                        )}
                                    </div>
                                )}

                                <div className="mt-5 pt-4 border-t border-slate-100 flex items-center gap-2">
                                    <Button
                                        size="sm"
                                        onClick={() => renew(r)}
                                        data-testid={`rental-renew-btn-${r.id}`}
                                        className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                                    >
                                        <RefreshCw className="w-4 h-4 mr-2" />
                                        Renouveler
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        data-testid={`rental-edit-btn-${r.id}`}
                                        onClick={() => {
                                            setEditing(r);
                                            setDialogOpen(true);
                                        }}
                                    >
                                        <Pencil className="w-4 h-4" />
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        data-testid={`rental-delete-btn-${r.id}`}
                                        onClick={() => remove(r)}
                                        className="text-slate-500 hover:text-rose-700 hover:bg-rose-50"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </Button>
                                </div>
                            </Card>
                        );
                    })}
                </div>
            </div>

            <RentalDialog
                open={dialogOpen}
                onOpenChange={setDialogOpen}
                editing={editing}
                onDone={load}
            />
        </>
    );
}
